import React, { useState, useRef, useEffect } from "react";
import "../../../styles/Part6Questions.css";

const Part6Questions = ({ questions, onBack }) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [hideMarked, setHideMarked] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const timerRef = useRef(null);
  const [viewMode, setViewMode] = useState("english"); // 'english', 'korean', 'mixed'
  const [showKoreanNouns, setShowKoreanNouns] = useState(true);
  const [showKoreanExplanation, setShowKoreanExplanation] = useState(false);
  const [showKoreanPassage, setShowKoreanPassage] = useState(false);
  const [showKoreanPassageBelow, setShowKoreanPassageBelow] = useState(false);
  const [isKoreanToggled, setIsKoreanToggled] = useState(true);
  const [showStudyGuide, setShowStudyGuide] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState("normal"); // "slow", "normal", "fast"

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  if (!questions || questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  const stopPlaying = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentTextIndex(0);
    setDisplayedText("");
  };

  const pausePlaying = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsPlaying(false);
    setIsPaused(true);
  };

  const getPlaybackInterval = (speedType) => {
    // 직접 전달된 속도 타입을 사용하거나, 없으면 현재 상태 값 사용
    const speed = speedType || playbackSpeed;

    switch (speed) {
      case "slow":
        return 600; // 느린 속도 (600ms)
      case "fast":
        return 300; // 빠른 속도 (300ms)
      case "normal":
      default:
        return 500; // 보통 속도 (500ms)
    }
  };

  const setSpeed = (speed) => {
    if (playbackSpeed === speed) return;

    // 새 속도 값을 직접 사용하여 인터벌 계산
    const newInterval = getPlaybackInterval(speed);

    setPlaybackSpeed(speed);

    if (isPlaying) {
      const currentIndex = currentTextIndex;
      const currentDisplayedText = displayedText;

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // 새 속도로 즉시 재생 시작 (setTimeout 제거)
      startPlayingWithCurrentState(currentIndex, currentDisplayedText, speed);
    }
  };

  const startPlayingWithCurrentState = (startIdx, currentText, speedType) => {
    const sourceText = showKoreanNouns
      ? currentQuestion.koreanNouns
      : currentQuestion.passage;

    // HTML 태그 제거하고 줄바꿈 유지
    const cleanText = sourceText.replace(/<\/?mark>/g, "");

    // 각 줄을 배열로 분리
    const lines = cleanText.split("\n");

    // 각 줄의 단어들을 분리하여 2차원 배열 생성
    const wordsByLine = lines.map((line) => line.trim().split(/\s+/));

    let startIndex = startIdx;
    let lineIndex = 0;
    let wordIndex = 0;
    let displayText = currentText;

    // 시작 위치 계산
    if (startIndex > 0) {
      let count = 0;
      for (let i = 0; i < wordsByLine.length; i++) {
        if (count + wordsByLine[i].length > startIndex) {
          lineIndex = i;
          wordIndex = startIndex - count;
          break;
        }
        count += wordsByLine[i].length;
      }
    }

    let interval = getPlaybackInterval(speedType);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      if (lineIndex < wordsByLine.length) {
        const currentLine = wordsByLine[lineIndex];

        if (wordIndex < currentLine.length) {
          // 현재 줄의 단어들을 추가
          const lineText = currentLine.slice(0, wordIndex + 1).join(" ");
          displayText = wordsByLine
            .slice(0, lineIndex)
            .map((line) => line.join(" "))
            .concat(lineText)
            .join("\n");

          wordIndex++;
          setCurrentTextIndex(startIndex + 1);
          startIndex++;
        } else {
          // 다음 줄로 이동
          lineIndex++;
          wordIndex = 0;
          displayText = wordsByLine
            .slice(0, lineIndex)
            .map((line) => line.join(" "))
            .join("\n");
        }

        setDisplayedText(displayText);
      } else {
        stopPlaying();
      }
    }, interval);
  };

  const startPlaying = (fromBeginning = false) => {
    if (isPlaying) {
      pausePlaying();
      return;
    }

    if (fromBeginning) {
      setCurrentTextIndex(0);
      setDisplayedText("");
    }

    setIsPlaying(true);
    setIsPaused(false);

    startPlayingWithCurrentState(
      fromBeginning ? 0 : currentTextIndex,
      fromBeginning ? "" : displayedText,
      playbackSpeed
    );
  };

  const renderPlaybackControls = () => {
    return (
      <div className="playback-controls">
        <button
          className="playback-button"
          onClick={() => startPlaying(false)}
          title={isPlaying ? "일시정지" : isPaused ? "계속 재생" : "재생"}
        >
          {isPlaying ? (
            <img
              src="/icons/pause.svg"
              alt="일시정지"
              className="control-icon"
            />
          ) : (
            <img src="/icons/play.svg" alt="재생" className="control-icon" />
          )}
        </button>

        <button
          className="playback-button"
          onClick={stopPlaying}
          disabled={!isPlaying && !isPaused}
          title="정지"
        >
          <img src="/icons/stop.svg" alt="정지" className="control-icon" />
        </button>

        <div className="speed-buttons">
          <button
            className={`speed-button ${
              playbackSpeed === "slow" ? "active" : ""
            }`}
            onClick={() => setSpeed("slow")}
            title="느린 속도"
          >
            초급
          </button>
          <button
            className={`speed-button ${
              playbackSpeed === "normal" ? "active" : ""
            }`}
            onClick={() => setSpeed("normal")}
            title="보통 속도"
          >
            중급
          </button>
          <button
            className={`speed-button ${
              playbackSpeed === "fast" ? "active" : ""
            }`}
            onClick={() => setSpeed("fast")}
            title="빠른 속도"
          >
            실전
          </button>
        </div>
      </div>
    );
  };

  const toggleSingleWord = (element) => {
    const currentText = element.textContent;
    const wordPairs = currentQuestion.wordPairs;

    if (wordPairs[currentText]) {
      element.textContent = wordPairs[currentText];
    } else {
      const koreanWord = Object.entries(wordPairs).find(
        ([kor, eng]) => eng === currentText
      )?.[0];
      if (koreanWord) {
        element.textContent = koreanWord;
      }
    }
  };

  const renderText = (text) => {
    if (!text) return null;
    const parsed = text.split(/(<mark>.*?<\/mark>)/g);
    return parsed.map((part, index) => {
      if (part.startsWith("<mark>")) {
        const content = part.replace(/<\/?mark>/g, "");
        return hideMarked ? (
          "     "
        ) : (
          <mark
            key={index}
            onClick={(e) => toggleSingleWord(e.target)}
            style={{
              cursor: "pointer",
              pointerEvents: "auto !important",
              backgroundColor: "transparent !important",
              color: "#3f41a1 !important",
              fontWeight: "500 !important",
              padding: "0 !important",
            }}
          >
            {content}
          </mark>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const handleAnswerSelect = (questionId, choice) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: choice,
    }));
  };

  const getPassageText = () => {
    if (isPlaying || isPaused) return displayedText;

    const textToShow = showKoreanNouns
      ? currentQuestion.koreanNouns
      : currentQuestion.passage;
    return renderText(textToShow);
  };

  const renderQuestions = () => {
    return currentQuestion.questions.map((q) => {
      const isAnswered = selectedAnswers[q.id];
      const isCorrect = selectedAnswers[q.id] === q.correctAnswer;

      return (
        <div key={q.id} className="question-item">
          <div className="question-number">문제 {q.id}</div>
          <div className="choices-container">
            {Object.entries(q.choices).map(([key, value]) => (
              <label key={key} className="choice-label">
                <input
                  type="radio"
                  name={`question-${q.id}`}
                  value={key}
                  checked={selectedAnswers[q.id] === key}
                  onChange={() => handleAnswerSelect(q.id, key)}
                  disabled={showResults}
                />
                <span className="choice-text">
                  {key}. {value}
                </span>
              </label>
            ))}
          </div>
          {showResults && (
            <div
              className={`result-feedback ${
                isAnswered && isCorrect ? "correct" : "incorrect"
              }`}
            >
              {isAnswered ? (
                isCorrect ? (
                  <>
                    <p>정답입니다!</p>
                    <p className="explanation">{q.explanation}</p>
                  </>
                ) : (
                  <>
                    <p>오답입니다. 정답은 {q.correctAnswer}번입니다.</p>
                    <p className="explanation">{q.explanation}</p>
                  </>
                )
              ) : (
                <>
                  <p>
                    답을 선택하지 않았습니다. 정답은 {q.correctAnswer}번입니다.
                  </p>
                  <p className="explanation">{q.explanation}</p>
                </>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setSelectedAnswers({});
      setShowResults(false);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      setSelectedAnswers({});
      setShowResults(false);
    }
  };

  const toggleKoreanToEnglish = () => {
    const markedElements = document.querySelectorAll("mark");

    markedElements.forEach((element) => {
      const currentText = element.textContent;
      const wordPairs = currentQuestion.wordPairs;

      if (isKoreanToggled) {
        const koreanWord = Object.entries(wordPairs).find(
          ([kor, eng]) => eng === currentText
        )?.[0];
        if (koreanWord) {
          element.textContent = koreanWord;
        }
      } else {
        if (wordPairs[currentText]) {
          element.textContent = wordPairs[currentText];
        }
      }
    });

    setIsKoreanToggled(!isKoreanToggled);
  };

  const toggleStudyGuide = () => {
    setShowStudyGuide(!showStudyGuide);
  };

  const StudyGuideModal = () => {
    if (!showStudyGuide) return null;

    const handleOverlayClick = (e) => {
      if (e.target.className === "study-guide-modal-overlay") {
        toggleStudyGuide();
      }
    };

    return (
      <div className="study-guide-modal-overlay" onClick={handleOverlayClick}>
        <div className="study-guide-modal">
          <div className="study-guide-header">
            <h2>Part 6,7 훈련 방법</h2>
            <button className="close-button" onClick={toggleStudyGuide}>
              ×
            </button>
          </div>
          <div className="study-guide-content">
            <video
              controls
              width="100%"
              autoPlay
              src="/videos/part67practice.mp4"
              type="video/mp4"
            >
              브라우저가 비디오 태그를 지원하지 않습니다.
            </video>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="part6-questions-container">
      <div className="part6-content">
        <div
          className={`passage-section ${
            showKoreanPassageBelow ? "with-translation" : ""
          }`}
        >
          <div className="passage-controls">
            <div className="left-controls">
              <button className="toggle-button" onClick={onBack}>
                ← 뒤로
              </button>
              <button
                className={`toggle-button ${showKoreanNouns ? "active" : ""}`}
                onClick={() => setShowKoreanNouns(!showKoreanNouns)}
              >
                {showKoreanNouns ? "한글단어" : "영어만"}
              </button>

              {renderPlaybackControls()}

              <button
                className={`toggle-button ${
                  showKoreanPassageBelow ? "active" : ""
                }`}
                onClick={() =>
                  setShowKoreanPassageBelow(!showKoreanPassageBelow)
                }
              >
                {showKoreanPassageBelow ? "한글 해설 숨기기" : "한글 해설 보기"}
              </button>
              <button className="study-guide-button" onClick={toggleStudyGuide}>
                공부 방법
              </button>
            </div>
            <div className="right-controls">
              <button
                className="toggle-button"
                onClick={handlePrevQuestion}
                disabled={currentQuestionIndex === 0}
              >
                이전 글
              </button>
              <span className="passage-counter">
                {currentQuestionIndex + 1} / {totalQuestions}
              </span>
              <button
                className="toggle-button"
                onClick={handleNextQuestion}
                disabled={currentQuestionIndex === totalQuestions - 1}
              >
                다음 글
              </button>
            </div>
          </div>

          <StudyGuideModal />

          <div className="passage-wrapper">
            <div className="passage-content">
              <div className="english-passage">{getPassageText()}</div>
            </div>
            {showKoreanPassageBelow && (
              <div className="korean-passage-side">
                {currentQuestion.translation}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Part6Questions;
